import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { usePresenterTheme } from "../../live/theme";
import { type NotesCommand, type NotesMessage, type NotesState, openChannel } from "../../rehearse/channel";
import { fmt } from "../../rehearse/clock";
import { ArrowIcon } from "./icons";
import { useKeyboard } from "./useKeyboard";
import "./notes.css";

/**
 * Speaker notes, in a second window on the presenter's laptop. It has no data of its own: it renders
 * the presenter window's state and drives it back (next, previous, timer, blank) over a BroadcastChannel.
 */
export function NotesPage() {
  const { deckId = "" } = useParams();
  usePresenterTheme();
  const [state, setState] = useState<NotesState | null>(null);
  const [waited, setWaited] = useState(false);
  const channel = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const ch = openChannel(deckId);
    channel.current = ch;
    if (!ch) return;
    ch.onmessage = (e: MessageEvent<NotesMessage>) => e.data.type === "state" && setState(e.data);
    ch.postMessage({ type: "hello" } satisfies NotesMessage);
    const t = window.setTimeout(() => setWaited(true), 1500);
    return () => {
      window.clearTimeout(t);
      ch.close();
    };
  }, [deckId]);

  useEffect(() => {
    document.title = state ? `Notes · ${state.nowTitle}` : "Speaker notes";
  }, [state]);

  const send = (action: NotesCommand["action"]) =>
    channel.current?.postMessage({ type: "command", action } satisfies NotesMessage);
  useKeyboard({
    " ": () => send("next"),
    ArrowRight: () => send("next"),
    PageDown: () => send("next"),
    ArrowLeft: () => send("prev"),
    PageUp: () => send("prev"),
    t: () => send("toggle-timer"),
    b: () => send("blank"),
  });

  if (!state)
    return (
      <div className="notes">
        <p className="notes-wait">
          {waited ? "Open the presenter for this deck first. This window follows it." : "Connecting to the presenter…"}
        </p>
      </div>
    );

  const over = state.section !== null && state.sectionMs > state.section.budgetMs;
  return (
    <div className="notes">
      <header className="notes-head">
        <span className="eyebrow">Speaker notes</span>
        <span className="notes-pos" data-testid="notes-position">
          {state.slideIndex + 1} / {state.slides}
        </span>
        {state.live && state.code && <span className="notes-code">{state.code}</span>}
      </header>

      <section className="notes-now">
        <span className="eyebrow">On screen</span>
        <h1 className="notes-title" data-testid="notes-now">
          {state.nowTitle}
        </h1>
      </section>

      <section className="notes-body" aria-label="Notes">
        {state.notes ? (
          <p className="notes-text" data-testid="notes-text">
            {state.notes}
          </p>
        ) : (
          <p className="notes-none">No notes for this slide.</p>
        )}
      </section>

      <footer className="notes-foot">
        <div className="notes-next">
          <span className="eyebrow">Next</span>
          <span className="notes-next-title" data-testid="notes-next">
            {state.nextTitle ?? "End of the deck"}
          </span>
        </div>
        <div className="notes-clock" data-testid="notes-clock">
          {state.section && (
            <span className="notes-time" data-over={over}>
              {String(state.section.number).padStart(2, "0")} · {fmt(state.sectionMs)} / {fmt(state.section.budgetMs)}
            </span>
          )}
          <span className="notes-time">
            Total {fmt(state.totalMs)} / {fmt(state.totalBudgetMs)}
          </span>
          <button type="button" className="btn" onClick={() => send("toggle-timer")} aria-keyshortcuts="T">
            {!state.started ? "Start timer" : state.running ? "Pause" : "Resume"}
          </button>
          <button type="button" className="btn" onClick={() => send("reset-timer")}>
            Reset
          </button>
        </div>
        <div className="notes-nav">
          <button type="button" className="btn" onClick={() => send("blank")} aria-keyshortcuts="B">
            Blank
          </button>
          <button type="button" className="btn btn-icon" onClick={() => send("prev")} aria-label="Previous">
            <ArrowIcon dir="left" />
          </button>
          <button type="button" className="btn btn-icon btn-next" onClick={() => send("next")} aria-label="Next">
            <ArrowIcon dir="right" />
          </button>
        </div>
      </footer>
    </div>
  );
}
